import { useSearch } from "@tanstack/react-router";
import { Modal, Tag } from "antd";
import { useGetTenantCatalogQuery } from "api/Tenant/hooks";
// The dedicated form-fields drawer is disabled for now — "Request access" behaves like
// "Request role" (direct request + confirmation modal), no contact/form data is collected.
// import { RequestProgramAccessDrawer } from "components/Drawers/RequestProgramAccessDrawer/RequestProgramAccessDrawer";
import { ProgramDetailDrawer } from "components/Drawers/ProgramDetailDrawer/ProgramDetailDrawer";
import { useTenantCategorySelectOptions } from "components/UI/Selects/TenantCategorySelectSearchable/useTenantCategorySelectOptions.ts";
import { ResponseWithPagination, Table } from "components/UI/Table";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import { DrawerId, TableId } from "types/models";

import { useProgramAccessAction } from "./useProgramAccessAction";

import TenantData = App.Data.TenantData;
import GetTenantsResponse = App.Http.Responses.Tenant.GetTenantsResponse;

interface SearchParams {
    ["programDetail-tenantId"]?: string;
}

export const ProgramsCatalogTable = () => {
    const tableId = TableId.ProgramsCatalogTable;
    const { t } = usePaldockTranslation("tables.programs-catalog");
    const { t: tGeneral } = usePaldockTranslation("general.tenant_category");

    const searchParams = useSearch({ strict: false }) as SearchParams & Record<string, unknown>;
    const programDetailTenantId = searchParams["programDetail-tenantId"];

    const tableAtomInstance = tableAtom<TenantData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));

    const { options: categoryFilterOptions } = useTenantCategorySelectOptions();

    const accessStatusFilterOptions = useMemo(
        () => [
            { label: t("filter.access_status.member"), value: "member" },
            { label: t("filter.access_status.pending"), value: "pending" },
            { label: t("filter.access_status.none"), value: "none" },
        ],
        [t]
    );

    const fetchCatalogQuery = useGetTenantCatalogQuery(
        {
            page: {
                number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
                size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
            },
            filter: {
                filter: tableState.filter?.filter ?? {},
                include: "catalogCategories",
                sort: tableState.filter?.sort ?? "title",
            },
        },
        { enabled: false }
    );

    const tableFetchDataUtils = useTableDataApiOperations<GetTenantsResponse, void, TenantData>(
        tableId,
        fetchCatalogQuery
    );

    const { renderAccessAction, awaitingApprovalTenant, setAwaitingApprovalTenant } = useProgramAccessAction(
        tableFetchDataUtils.refetchData
    );

    const { getRowHandlers, getRowClassName } = useRowHandlers<TenantData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.ProgramDetail,
                drawerParam: "programDetail-tenantId",
            },
        ],
    });

    const columnConfig: TableColumnType<TenantData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.logo")} />,
            dataIndex: "logo",
            key: "logo",
            render: (_, record) =>
                record.logo ? (
                    <img src={record.logo} alt="Logo" style={{ height: "32px" }} className="object-contain" />
                ) : (
                    <EmptyCell />
                ),
            width: 80,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.name")} />,
            dataIndex: "title",
            key: "title",
            sorter: true,
            ellipsis: true,
            width: 250,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.category")} />,
            dataIndex: "catalogCategories",
            key: "catalogCategories.category",
            render: (_, record) => {
                const categories = record.catalogCategories ?? [];
                const extraCount = Math.max(categories.length - 1, 0);

                if (!categories[0]) {
                    return <EmptyCell />;
                }

                return (
                    <div className="flex items-center gap-2">
                        <Tag>{tGeneral(categories[0].category)}</Tag>
                        {extraCount > 0 && <Tag className="m-0">+{extraCount}</Tag>}
                    </div>
                );
            },
            ellipsis: false,
            sorter: true,
            width: 300,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "catalogCategories.category",
            filterOptions: categoryFilterOptions,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.access")} />,
            dataIndex: "id",
            key: "user_access_status",
            width: 180,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "user_access_status",
            filterOptions: accessStatusFilterOptions,
            render: (_, record) => renderAccessAction(record),
        },
    ];

    return (
        <>
            <Table<ResponseWithPagination, TenantData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetTenantsResponse}
                dataSource={tableFetchDataUtils.tableData?.tenants || []}
                rowKey={(record) => record.id}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                enableFilters={true}
                loading={tableFetchDataUtils.fetchQuery.isFetching}
                autoScrollWidth={true}
                onRow={getRowHandlers}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
            />
            {programDetailTenantId && <ProgramDetailDrawer tenantId={programDetailTenantId} />}
            <Modal
                open={awaitingApprovalTenant !== null}
                onCancel={() => setAwaitingApprovalTenant(null)}
                onOk={() => setAwaitingApprovalTenant(null)}
                title={awaitingApprovalTenant?.title}
                cancelButtonProps={{ style: { display: "none" } }}
            >
                {t("messages.request_submitted")}
            </Modal>
            {/* Form-fields drawer disabled for now, see import comment above.
            {tenantId && tenantSlug && (
                <RequestProgramAccessDrawer
                    tenantId={tenantId}
                    tenantSlug={tenantSlug}
                    onSuccess={tableFetchDataUtils.refetchData}
                />
            )}
            */}
        </>
    );
};
